/**
 * Function-key shortcuts.
 *
 * The strip along the bottom of the POS screen - F1 Help, F2 Hold Order,
 * F3 Recall, F4 Table Layout, F5 Customer, F6 Quick Check - is not decoration.
 * A cashier working a lunch rush drives the till from the keyboard, and reaching
 * for a mouse costs seconds per order.
 *
 * The browser owns some of these by default (F3 is find, F5 is reload), so
 * preventDefault is essential: an F5 that reloads the page mid-order would lose
 * the basket.
 */

import { useEffect, useRef } from 'react';

/** Should a keystroke be ignored because the user is typing? */
const isTypingTarget = (target) => {
    if (!target) return false;
    const tag = target.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
};

/**
 * @param {Record<string, Function>} bindings  key -> handler, e.g. { F2: hold }
 * @param {object} [options]
 * @param {boolean} [options.enabled=true]
 * @param {boolean} [options.allowInInputs=false]  fire even while typing
 */
export const useHotkeys = (bindings, { enabled = true, allowInInputs = false } = {}) => {
    // Held in a ref so the effect does not resubscribe on every render - the
    // handlers close over state that changes constantly on the POS screen.
    const bindingsRef = useRef(bindings);
    bindingsRef.current = bindings;

    useEffect(() => {
        if (!enabled) return undefined;

        const onKeyDown = (event) => {
            const map = bindingsRef.current || {};

            // Build the key name: 'F2', 'Ctrl+S', 'Escape'
            const parts = [];
            if (event.ctrlKey) parts.push('Ctrl');
            if (event.altKey) parts.push('Alt');
            if (event.shiftKey && event.key.length > 1) parts.push('Shift');
            parts.push(event.key.length === 1 ? event.key.toUpperCase() : event.key);
            const combo = parts.join('+');

            const handler = map[combo] || map[event.key];
            if (!handler) return;

            // Function keys still work while typing - F2 to hold an order should
            // not require leaving the search box first. Letter shortcuts do not.
            const isFunctionKey = /^F\d{1,2}$/.test(event.key);
            if (!allowInInputs && !isFunctionKey && isTypingTarget(event.target)) return;

            event.preventDefault();
            event.stopPropagation();
            handler(event);
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [enabled, allowInInputs]);
};

export default useHotkeys;
